import type { CSSProperties } from 'react';

import type { StringOrNode } from '@snowit/icetypes';
import type { IDivider } from '../Divider/Divider';
import type { ITypo } from '../Typography/Typo';

import testId from '@snowit/utils/testId';

import Divider from '../Divider/Divider';
import Typo from '../Typography/Typo';

import cx from 'classnames';
import s from './Grid.module.scss';

export interface IGridCell<T extends string = string> {
  key: T;
  content?: StringOrNode;
  columnStart?: number;
  columnSpan?: number;
  rowStart?: number;
  rowSpan?: number;
  size?: number;
  placeSelf?: string[];
  hide?: boolean;
}

type GridCellProps = {
  cell: IGridCell;
  id?: string;
  test?: string;
};

export const makeGridDivider = (
  key: string,
  props?: IDivider & { size?: number; hidden?: boolean },
): IGridCell => {
  const { size, hidden, spacing = [0.5], ...rest } = props ?? {};
  return {
    key,
    size,
    content: <Divider {...rest} spacing={spacing} />,
    hide: hidden,
  };
};

export const makeGridTitle = (
  text: string,
  props?: {
    key?: string;
    size?: number;
    hidden?: boolean;
    typo?: Omit<ITypo, 'text'>;
  },
): IGridCell => {
  const { key, size, hidden, typo } = props ?? {};

  return {
    key: key ?? text,
    size,
    content: (
      <Typo
        text={text.toUpperCase()}
        tag={'h3'}
        size={'md'}
        color={'primary'}
        weight={'medium'}
        {...typo}
      />
    ),
    hide: hidden,
  };
};

const GridCell = (props: GridCellProps) => {
  const { cell, test } = props;

  const { content, columnStart, columnSpan, rowStart, rowSpan, hide } = cell;

  const renderContent = () => {
    if (typeof content === 'function') return content();
    return content;
  };

  let placeSelf = '';
  if (cell.placeSelf?.length) {
    placeSelf = [cell.placeSelf[0], cell.placeSelf[1] ?? cell.placeSelf[0]].join(' ');
  }

  return (
    <div
      id={`snowit-grid-${cell.key}`}
      className={cx(s.cell, { [s.hide]: hide })}
      {...testId(test, cell.key)}
      key={cell.key}
      style={
        {
          '--column-start': columnStart,
          '--column-span': columnSpan,
          '--row-start': rowStart,
          '--row-span': rowSpan,
          '--place-self': placeSelf,
        } as CSSProperties
      }
    >
      {renderContent()}
    </div>
  );
};

export default GridCell;
